import type { ProductExportRow, ProductType, ProductUnit } from '@/api/models';
import { formatInLanguage } from '@/utils';
import { PRODUCT_FILTER_DESCRIPTORS } from '../productFilterDescriptors';
import {
    FILTERS_COPY,
    describeAppliedFilterTag,
    type AppliedFilterTag,
    type FilterWords,
} from '../productFilters/productFiltersMeta';
import type { StockFiguresScope } from '../productFilters/branchScopeMeta';

/**
 * The products export (bos-app#120, design boards **Main** and **Export**):
 * the workbook's columns, its file name, the Filters sheet's lines and the
 * notices a run ends with. Pure, so the hook and the workbook builder share
 * one spec.
 */

const EXPORT_COPY = 'products.export';

/** A column of the Products sheet. */
export type ProductExportColumnKey =
    | 'productName'
    | 'sku'
    | 'type'
    | 'category'
    | 'brand'
    | 'variant'
    | 'barcode'
    | 'alternateBarcodes'
    | 'unit'
    | 'quantity'
    | 'expiredQuantity'
    | 'minimumQuantity'
    | 'currentCost'
    | 'salePrice'
    | 'markup'
    | 'wholesalePrice'
    | 'lowStock'
    | 'archived'
    | 'draft';

/**
 * How a column's cells are written: text; a plain number; a number in ₾;
 * a fraction in percent format.
 */
export type ProductExportColumnKind = 'text' | 'number' | 'money' | 'percent';

export type ProductExportColumn = {
    key: ProductExportColumnKey;
    labelKey: string;
    /** In Excel's character widths. */
    width: number;
    kind: ProductExportColumnKind;
};

const column = (
    key: ProductExportColumnKey,
    width: number,
    kind: ProductExportColumnKind,
): ProductExportColumn => ({
    key,
    labelKey: `${EXPORT_COPY}.columns.${key}`,
    width,
    kind,
});

/**
 * The Products sheet's columns, in order. Markup is the export's own.
 * Alternate barcodes sit beside the main one and Draft beside Archived
 * (API-195, bos-app#227).
 */
export const PRODUCT_EXPORT_COLUMNS: readonly ProductExportColumn[] = [
    column('productName', 36, 'text'),
    column('sku', 16, 'text'),
    column('type', 12, 'text'),
    column('category', 32, 'text'),
    column('brand', 18, 'text'),
    column('variant', 20, 'text'),
    column('barcode', 18, 'text'),
    column('alternateBarcodes', 28, 'text'),
    column('unit', 12, 'text'),
    column('quantity', 11, 'number'),
    column('expiredQuantity', 11, 'number'),
    column('minimumQuantity', 11, 'number'),
    column('currentCost', 13, 'money'),
    column('salePrice', 13, 'money'),
    column('markup', 11, 'percent'),
    column('wholesalePrice', 13, 'money'),
    column('lowStock', 11, 'text'),
    column('archived', 11, 'text'),
    column('draft', 11, 'text'),
];

/** Money cells: a number, shown as the app shows GEL ("₾ 1,234.56"). */
export const EXPORT_MONEY_FORMAT = '"₾ "#,##0.00';

/** Markup cells: a fraction, shown as a percent with the API's one decimal. */
export const EXPORT_PERCENT_FORMAT = '0.0%';

/**
 * Everything the workbook says in the reader's language, resolved by the
 * View: the builder is pure of i18next, so it tests without it.
 */
export type ProductExportLabels = {
    sheets: { products: string; filters: string };
    /** Header per column; a missing one falls back to the column's key. */
    columns: Partial<Record<ProductExportColumnKey, string>>;
    types: Record<ProductType, string>;
    /** A unit's name; a missing one falls back to the wire value. */
    units: Partial<Record<ProductUnit, string>>;
    yes: string;
    no: string;
    /** Between a category path's names, as the category picker reads them. */
    categorySeparator: string;
    filters: {
        /** The Filters sheet's header row. */
        heading: { label: string; value: string };
        /** One line per filter. */
        lines: ProductExportFilterSheetLine[];
        /** The label of the last line, when the file was made. */
        exportedAt: string;
    };
};

/** A line of the Filters sheet, in words. */
export type ProductExportFilterSheetLine = { label: string; value: string };

/**
 * A line of the Filters sheet before the View translates it: what it is about
 * and what it holds, several values read as one list ("A, B").
 */
export type ProductExportFilterLine = {
    labelKey: string;
    values: FilterWords[];
};

/** The label of the Filters sheet's last line, when the file was made. */
export const EXPORTED_AT_LABEL_KEY = `${EXPORT_COPY}.filters.exportedAt`;

const NONE: FilterWords = { key: `${EXPORT_COPY}.filters.none` };

/**
 * The Filters sheet's lines, before translation: the branch whose stock the
 * figures count, the search ("None" without one), then each applied filter as
 * its tag reads, in full, or one line saying there is none. The branch has a
 * line of its own, so its tag is left out.
 */
export const describeExportFilters = ({
    stockFigures,
    search,
    tags,
}: {
    stockFigures: StockFiguresScope;
    search: string;
    tags: readonly AppliedFilterTag[];
}): ProductExportFilterLine[] => {
    // "Stock figures for: All branches", as the drawer's branch field reads.
    const branch: ProductExportFilterLine = {
        labelKey: `${FILTERS_COPY}.branchLabel`,
        values: [
            stockFigures.kind === 'branch'
                ? { text: stockFigures.name }
                : stockFigures.kind === 'allBranches'
                  ? { key: `${FILTERS_COPY}.allBranches` }
                  : { key: `${FILTERS_COPY}.unknownValue` },
        ],
    };
    const searchLine: ProductExportFilterLine = {
        labelKey: `${EXPORT_COPY}.filters.search`,
        values: [search ? { text: search } : NONE],
    };
    const filterLines = tags
        .filter((tag) => PRODUCT_FILTER_DESCRIPTORS[tag.group].exportAsFilter)
        .map((tag): ProductExportFilterLine => {
            const { nameKey, values } = describeAppliedFilterTag(tag);
            // A switch's tag is its name alone ("Low stock only"); a sheet
            // line needs a value beside it.
            return {
                labelKey: nameKey,
                values:
                    values.length === 0
                        ? [{ key: `${EXPORT_COPY}.workbook.yes` }]
                        : values,
            };
        });
    const noFilters: ProductExportFilterLine[] =
        filterLines.length === 0
            ? [{ labelKey: `${EXPORT_COPY}.filters.filters`, values: [NONE] }]
            : [];
    return [branch, searchLine, ...filterLines, ...noFilters];
};

/** The file's name, dated in the reader's own day: `products-2026-09-24.xlsx`. */
export const productExportFilename = (now: Date): string =>
    `products-${formatInLanguage(now.toISOString(), 'YYYY-MM-DD', undefined)}.xlsx`;

/**
 * When the file was made, for its Filters sheet, in the app's display format
 * (`DD/MM/YYYY`) with the time, in the reader's zone. All digits, so the UI
 * language doesn't change it.
 */
export const formatExportedAt = (now: Date): string =>
    formatInLanguage(now.toISOString(), 'DD/MM/YYYY HH:mm', undefined);

/** How many products a file holds: the rows are variants. */
export const countExportedProducts = (
    rows: readonly Pick<ProductExportRow, 'productId'>[],
): number => new Set(rows.map((row) => row.productId)).size;

/** bos-api's refusal of an export over 20,000 rows. */
export const EXPORT_TOO_LARGE = 'EXPORT_TOO_LARGE';

/** A notice's copy, for the View to translate. */
export type ProductExportFailure = { kind: 'localized'; key: string };

const TOO_LARGE_NOTICE: ProductExportFailure = {
    kind: 'localized',
    key: 'products.export.notice.tooLarge',
};

const FAILED_NOTICE: ProductExportFailure = {
    kind: 'localized',
    key: 'products.export.notice.failed',
};

/**
 * What a failed export tells the reader: "too many rows, narrow the filters"
 * for bos-api's `EXPORT_TOO_LARGE`, and a plain failure for anything else
 * (rulebook §7: never the server's prose).
 */
export const exportFailureNotice = (error: unknown): ProductExportFailure =>
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === EXPORT_TOO_LARGE
        ? TOO_LARGE_NOTICE
        : FAILED_NOTICE;

/** How the last export ended, until the reader dismisses it or exports again. */
export type ProductExportNotice =
    | { kind: 'saved'; productCount: number; filename: string }
    | { kind: 'failed'; failure: ProductExportFailure };
